import { useState } from "react";
import type { Corner } from "../lib/api";
import Button from "../ui/Button";
import Spinner from "../ui/Spinner";
import { IconImage, IconX } from "../ui/icons";

/**
 * A small inline picker that lets the user apply a logo to one of four
 * corners, or remove it. Calls `onApply` with the chosen corner (or null).
 */
export default function LogoCornerPicker({
  currentCorner,
  hasLogo,
  onApply,
}: {
  currentCorner: Corner | null;
  hasLogo: boolean;
  onApply: (corner: Corner | null) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!hasLogo) {
    return (
      <Button
        variant="ghost"
        size="sm"
        disabled
        iconLeft={<IconImage size={12} />}
        title="Upload a brand logo in Settings first"
      >
        Add logo (in Settings)
      </Button>
    );
  }

  const apply = async (corner: Corner | null) => {
    setBusy(true);
    try {
      await onApply(corner);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setOpen(true)}
        iconLeft={<IconImage size={12} />}
      >
        {currentCorner ? `Logo: ${cornerLabel(currentCorner)}` : "Add logo"}
      </Button>
    );
  }

  const cornerBtn = (c: Corner, label: string) => {
    const selected = currentCorner === c;
    return (
      <button
        type="button"
        onClick={() => apply(c)}
        disabled={busy}
        className={[
          "px-3 py-2 text-xs font-medium border rounded-md transition-colors",
          "disabled:opacity-50",
          selected
            ? "border-brand-500 bg-brand-50 text-brand-800"
            : "border-ink-200 bg-white text-ink-700 hover:bg-ink-50 hover:border-ink-300",
        ].join(" ")}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="inline-block bg-white border border-ink-100 rounded-lg p-3 shadow-sm shadow-ink-900/5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-ink-700">
          Place logo in corner
        </p>
        <button
          onClick={() => setOpen(false)}
          className="text-ink-400 hover:text-ink-700"
        >
          <IconX size={14} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-1.5 mb-2">
        {cornerBtn("tl", "Top left")}
        {cornerBtn("tr", "Top right")}
        {cornerBtn("bl", "Bottom left")}
        {cornerBtn("br", "Bottom right")}
      </div>
      {currentCorner && (
        <button
          type="button"
          onClick={() => apply(null)}
          disabled={busy}
          className="w-full px-3 py-1.5 text-xs border border-ink-200 rounded-md hover:bg-ink-50 disabled:opacity-50 text-ink-700"
        >
          Remove logo
        </button>
      )}
      {busy && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-ink-500">
          <Spinner size={12} /> Compositing… refresh in a few seconds.
        </div>
      )}
    </div>
  );
}

function cornerLabel(c: Corner): string {
  return (
    { tl: "top-left", tr: "top-right", bl: "bottom-left", br: "bottom-right" }[
      c
    ] ?? c
  );
}
